import type { Label } from "@app/core";
import type { StorageAdapter } from "../adapter/adapter.js";
import { clampAck, type SyncDestination } from "./mut.js";

const LABEL_CONTENT_SET = `name=?,color=?,updated_at=?,deleted_at=?`;
const LABEL_UPDATE_SQL = `UPDATE labels SET ${LABEL_CONTENT_SET},mut=mut+1 WHERE id=?`;
const LABEL_UPDATE_SERVER_SQL = `UPDATE labels SET ${LABEL_CONTENT_SET},mut=mut+1,server_acked_mut=mut+1 WHERE id=?`;

/** CRUD over the `labels` table. Returns plain Label objects. Labels never reach
 *  Google, so they carry only the server ack column. */
export class LabelsRepository {
  constructor(private readonly db: StorageAdapter) {}

  /** `synced: "server"` marks the row as already in step with the sync server
   *  (an inbound apply); a plain insert starts dirty. */
  async insert(l: Label, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      `INSERT INTO labels (id,name,color,mut,server_acked_mut,created_at,updated_at,deleted_at)
       VALUES (?,?,?,?,?,?,?,?)`,
      [l.id, l.name, l.color ?? null, 1, synced === "server" ? 1 : 0, l.created_at, l.updated_at, l.deleted_at],
    );
  }

  /** Every content write bumps `mut` (the sync engine's per-row dirty fact);
   *  `synced: "server"` self-acknowledges an inbound sync apply. */
  async update(l: Label, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      synced === "server" ? LABEL_UPDATE_SERVER_SQL : LABEL_UPDATE_SQL,
      [l.name, l.color ?? null, l.updated_at, l.deleted_at, l.id],
    );
  }

  /** Clamped acknowledgement — see `TasksRepository.ackMut`. */
  async ackMut(id: string, dest: SyncDestination, upTo: number): Promise<void> {
    if (dest !== "server") return;
    const current = await this.findById(id);
    if (!current) return;
    const next = clampAck(current, dest, upTo);
    if (next === null) return;
    await this.db.execute(`UPDATE labels SET server_acked_mut=? WHERE id=?`, [next, id]);
  }

  async listActive(): Promise<Label[]> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM labels WHERE deleted_at IS NULL`,
    );
    return rows.map(fromRow);
  }

  /** Every row INCLUDING soft-deleted tombstones — for sync. */
  async listAll(): Promise<Label[]> {
    const rows = await this.db.select<Record<string, unknown>>(`SELECT * FROM labels`);
    return rows.map(fromRow);
  }

  async findById(id: string): Promise<Label | null> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM labels WHERE id=?`, [id],
    );
    return rows[0] ? fromRow(rows[0]) : null;
  }

  async hardDelete(id: string): Promise<void> {
    await this.db.execute(`DELETE FROM labels WHERE id=?`, [id]);
  }
}

function fromRow(r: Record<string, unknown>): Label {
  return {
    id: String(r["id"]),
    name: String(r["name"] ?? ""),
    color: (r["color"] as string | null) ?? null,
    mut: Number(r["mut"] ?? 0),
    server_acked_mut: Number(r["server_acked_mut"] ?? 0),
    created_at: String(r["created_at"]),
    updated_at: String(r["updated_at"]),
    deleted_at: (r["deleted_at"] as string | null) ?? null,
  };
}
